'use client'
import React, { useRef } from 'react'
import { NuqsAdapter } from 'nuqs/adapters/next/app'
import { ICinematics } from '@/types'
import { ReactQueryDevtools } from '@tanstack/react-query-devtools'
import type { DehydratedState } from '@tanstack/react-query'
import { RecaptchaProvider } from './providers/RecaptchaProvider'
import { QueryProvider } from './providers/QueryProvider'
import { AppProvider } from './providers/AppProvider'
import { FirebaseProvider } from './providers/FirebaseProvider'
import { useCinematicsStore } from './stores/CinematicsStore'
import { OptimizelyProvider } from './providers/OptimizelyProvider'
import { CinematicsRecovery } from './CinematicsRecovery'
import { shouldUseLocalCinematics } from '@/utils/shouldUseLocalCinematics'

const isDevelopment = process.env.NODE_ENV === 'development'

interface IFilmProps {
  children: React.ReactNode
  cinematics?: ICinematics
  cinematicsMetadata: { tag: string; type: string }
  initialLanguage: string
  localCinematics?: ICinematics
  dehydratedState?: DehydratedState
}

export const Film: React.FC<IFilmProps> = (props) => {
  const { children, initialLanguage, cinematics, cinematicsMetadata, localCinematics, dehydratedState } = props
  const { initialize, setPath, cinematics: cinematicsStore } = useCinematicsStore()
  const { tag, type } = cinematicsMetadata

  const useLocal = shouldUseLocalCinematics(localCinematics)
  const cinematicsToUse = useLocal ? localCinematics! : cinematics

  // Synchronous initialization: populate the store before children render.
  // Uses the React-sanctioned lazy ref init pattern (ref.current === null check).
  const storeInitialized = useRef<boolean | null>(null)
  if (storeInitialized.current === null) {
    if (cinematicsToUse) {
      initialize(cinematicsToUse, initialLanguage, useLocal)
      storeInitialized.current = true
      if (isDevelopment) {
        console.log(`%cLoaded cinematics: ${useLocal ? 'local' : `${tag} (${type})`}`, 'color: springgreen; font-weight: bold')
      }
    } else if (isDevelopment) {
      console.log(`%cCinematics not found: ${tag} (${type})`, 'color: red; font-weight: bold; font-size: 16px')
    }
  }

  return (
    <FirebaseProvider>
      <RecaptchaProvider>
        <QueryProvider dehydratedState={dehydratedState}>
          <OptimizelyProvider>
            <AppProvider
              cinematics={cinematicsStore}
              setPath={setPath}
            >
              <NuqsAdapter>
                <CinematicsRecovery tag={tag} initialLanguage={initialLanguage}>
                  {children}
                </CinematicsRecovery>
              </NuqsAdapter>
            </AppProvider>
          </OptimizelyProvider>
          <ReactQueryDevtools initialIsOpen={false} />
        </QueryProvider>
      </RecaptchaProvider>
    </FirebaseProvider >
  )
}